import * as React from 'react';
import cn from 'classnames';
import styles from './SettingsNavigation.module.scss';
import messages from './messages';
import { FormattedMessage } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';
import { CaretLeft, CaretRight } from 'src/common/icons';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import useRoleManager from 'src/hooks/useRoleManager';
import BoardingTour from '../BoardingTour';



type SettingsNavigationProps = {
  className?: string;
};

export const SettingsNavigation: React.FC<SettingsNavigationProps> = ({ className }) => {
  const { isAdmin } = useRoleManager();
  const location = useLocation();
  const navRef = React.useRef<HTMLElement>(null);

  // When the strip overflows on narrow viewports we surface gradient
  // hints + chevron icons on whichever side(s) the user can still
  // scroll towards. When both flags are false (wide viewport, all tabs
  // visible) no hints render — the CSS is opacity-driven so the
  // transition is smooth as content scrolls past either end.
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const { isLoading, data, isError } = useSettingsQuery();
  
  // Hide Storage tab for platform-managed (r2) accounts — EXCEPT when the
  // plan includes BYO storage (SCALE/Enterprise, plan-structure v2): entitled
  // accounts must always see the tab. NOTE: for an r2 account the provider
  // flip to their own S3 is still a provisioning step — the r2->S3 switch
  // workflow is a Phase 8 follow-up (plan-structure-impl-plan.md).
  const byosEntitled =
    data?.addons?.BYO_STORAGE?.added === true &&
    data?.addons?.BYO_STORAGE?.enabled === true;
  const shouldShowStorageTab =
    !isLoading && !isError &&
    (data?.storage?.storage_provider !== 'r2' || byosEntitled);
  
  // Show Tools tab if AUDIO_SUBTITLES addon is added
  const shouldShowToolsTab = !isLoading && !isError && data?.addons?.AUDIO_SUBTITLES?.added === true;

  // Built-in feature: the AI tab is always shown to admins regardless of the
  // AI addon — the page renders read-only (controls disabled) when the addon
  // isn't enabled or the backend API is unavailable. Still admin-only because
  // the underlying /api/v1/ai/* endpoints 401 for managers/users, which the
  // shared error handler interprets as session expiry and would log them out.
  const shouldShowAiTab = isAdmin && !isLoading && !isError;

  // On narrow viewports the tab strip scrolls horizontally (see
  // `SettingsNavigation.module.scss`). Whenever the route changes,
  // bring the active tab into view so users always land on the
  // visible part of the strip — otherwise navigating from a tab on
  // the right (e.g. Database) to one on the left (e.g. Storage) would
  // leave the active tab hidden off-screen.
  React.useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>(`.${styles.active}`);
    if (!active) return;
    // `inline: 'center'` centres the active tab in the strip when the
    // strip is scrollable. On non-scrollable widths this is a no-op.
    active.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [location.pathname]);

  // Recompute the "can scroll" flags on every scroll, resize, and
  // content change (tabs may appear/disappear as `useSettingsQuery`
  // resolves). The 1px slop accommodates fractional pixel rounding
  // — without it `canScrollRight` can stay stuck at `true` even after
  // the user has scrolled all the way to the right edge.
  React.useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = nav;
      setCanScrollLeft(scrollLeft > 1);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    update();
    nav.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(nav);

    return () => {
      nav.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  return (
    <><BoardingTour title='Settings' description='This page shows the Settings' path='/admin/settings' uniqueId='SETTINGS' />
    <div className={cn([styles.SettingsNavigation, className])}>
      <div
        className={cn(styles.navWrapper, {
          [styles.canScrollLeft]: canScrollLeft,
          [styles.canScrollRight]: canScrollRight,
        })}
      >
        <span className={styles.scrollHintLeft} aria-hidden="true">
          <CaretLeft />
        </span>
        <nav ref={navRef}>
        {shouldShowAiTab && (
          <NavLink to={'/admin/settings/ai'} activeClassName={styles.active}>
            <FormattedMessage {...messages.aiAgent} />
          </NavLink>
        )}
        {shouldShowStorageTab && (
          <NavLink to={'/admin/settings/storage'} activeClassName={styles.active}>
            <FormattedMessage {...messages.storage} />
          </NavLink>
        )}
        <NavLink to={'/admin/settings/security'} activeClassName={styles.active}>
          <FormattedMessage {...messages.security} />
        </NavLink>
        <NavLink to={'/admin/settings/image'} activeClassName={styles.active}>
          <FormattedMessage {...messages.image} />
        </NavLink>
        <NavLink to={'/admin/settings/watermark'} activeClassName={styles.active}>
          <FormattedMessage {...messages.watermark} />
        </NavLink>
        <NavLink to={'/admin/settings/video'} activeClassName={styles.active}>
          <FormattedMessage {...messages.video} />
        </NavLink>
        <NavLink to={'/admin/settings/webhook'} activeClassName={styles.active}>
          <FormattedMessage {...messages.webhook} />
        </NavLink>
        <NavLink to={'/admin/settings/database'} activeClassName={styles.active}>
          <FormattedMessage {...messages.database} />
        </NavLink>
        <NavLink to={'/admin/settings/metadata'} activeClassName={styles.active}>
          <FormattedMessage {...messages.metadata} />
        </NavLink>
        <NavLink to={'/admin/settings/versions'} activeClassName={styles.active}>
          <FormattedMessage {...messages.versions} />
        </NavLink>
        {isAdmin && (
          <NavLink to={'/admin/settings/notifications'} activeClassName={styles.active}>
            <FormattedMessage {...messages.notifications} />
          </NavLink>
        )}
        </nav>
        <span className={styles.scrollHintRight} aria-hidden="true">
          <CaretRight />
        </span>
      </div>
    </div>
    </>
  );
};

export default SettingsNavigation;
